import { Link, useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { cn, formatUserName, getInitials } from '@/lib/utils'
import { User, UserRole } from '@/types/entities'
import {
  Home,
  Image,
  DollarSign,
  Upload,
  Users,
  FileDown,
  Receipt,
  LogOut,
  UserCog,
  ListTodo,
  Palette,
  Brush,
  BarChart3,
  Layout,
  Layers,
  FileText,
  Link2,
  Link as LinkIcon,
  Sparkles,
  Calendar,
  Megaphone,
  Headset,
  LayoutGrid,
  Truck,
  UserSearch,
  Globe,
  ShieldCheck,
  BookOpen,
  Mail,
  ChevronDown,
  Network,
  Rss,
  TrendingUp,
  Search,
  Package,
  Target,
  Waypoints,
  Bot,
  GitPullRequest,
  Activity,
  Wand2,
  History,
} from 'lucide-react'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Separator } from '@/components/ui/separator'
import { useCommissionsAccess } from '@/features/easel/hooks'
import { useFeatures } from '@/features/flags'

interface SidebarProps {
  open: boolean
  onToggle: () => void
  user: User
}

interface NavItem {
  to: string
  label: string
  icon: React.ReactNode
  exact?: boolean
  excludeActivePaths?: string[]
  badge?: string
  children?: NavItem[]
  /**
   * Feature flag this item is gated on. Absent means always shown.
   *
   * An item whose flag is not enabled for this account is removed from the sidebar entirely
   * rather than disabled: a greyed-out row still advertises unreleased work, and five of them in
   * a row is what made 38% of this menu a dead end.
   */
  flag?: string
}

interface NavGroup {
  label: string
  items: NavItem[]
}

function getNavigationGroups(role: UserRole): NavGroup[] {
  if (role === UserRole.MANAGER) {
    return [
      {
        label: 'Admin',
        items: [
          {
            to: '/app',
            label: 'Dashboard',
            icon: <Home className="h-[18px] w-[18px]" />,
            exact: true,
          },
          {
            to: '/app/artworks',
            label: 'Artworks',
            icon: <Image className="h-[18px] w-[18px]" />,
            children: [
              {
                to: '/app/export-artworks',
                label: 'Approvals',
                icon: <ShieldCheck className="h-[18px] w-[18px]" />,
              },
            ],
          },
          {
            to: '/app/artists',
            label: 'Artists',
            icon: <Palette className="h-[18px] w-[18px]" />,
            children: [
              {
                to: '/app/prospects',
                label: 'Prospects',
                icon: <UserSearch className="h-[18px] w-[18px]" />,
                excludeActivePaths: ['/app/prospects/claims'],
              },
              {
                to: '/app/prospects/claims',
                label: 'Account claims',
                icon: <UserCog className="h-[18px] w-[18px]" />,
              },
              {
                to: '/app/external',
                label: 'External',
                icon: <Globe className="h-[18px] w-[18px]" />,
              },
              {
                to: '/app/export-artists',
                label: 'Export artists',
                icon: <FileDown className="h-[18px] w-[18px]" />,
              },
            ],
          },
          {
            // The parent is the search console (and a session opened in it, /s/:id); the
            // session list and shopper searches highlight only themselves.
            to: '/app/picasso',
            label: 'Picasso',
            icon: <Wand2 className="h-[18px] w-[18px]" />,
            excludeActivePaths: ['/app/picasso/sessions', '/app/picasso/shopper'],
            children: [
              {
                to: '/app/picasso/sessions',
                label: 'Your sessions',
                icon: <History className="h-[18px] w-[18px]" />,
              },
              {
                to: '/app/picasso/shopper',
                label: 'Shopper searches',
                icon: <Users className="h-[18px] w-[18px]" />,
              },
            ],
          },
          {
            // The parent is the overview; the sub-pages share the prefix,
            // so they are excluded here and highlight only themselves.
            to: '/app/knowledge',
            label: 'Knowledge',
            icon: <Network className="h-[18px] w-[18px]" />,
            excludeActivePaths: ['/app/knowledge/operations', '/app/knowledge/domains', '/app/knowledge/concepts', '/app/knowledge/graph', '/app/knowledge/agent', '/app/knowledge/proposals', '/app/knowledge/progress'],
            children: [
              {
                to: '/app/knowledge/operations',
                label: 'Operations & resolutions',
                icon: <Activity className="h-[18px] w-[18px]" />,
              },
              {
                to: '/app/knowledge/domains',
                label: 'Research domains',
                icon: <Network className="h-[18px] w-[18px]" />,
              },
              {
                to: '/app/knowledge/concepts',
                label: 'Concept catalogue',
                icon: <Search className="h-[18px] w-[18px]" />,
              },
              {
                to: '/app/knowledge/graph',
                label: 'Graph explorer',
                icon: <Waypoints className="h-[18px] w-[18px]" />,
              },
              {
                to: '/app/knowledge/agent',
                label: 'Resolution agent',
                icon: <Bot className="h-[18px] w-[18px]" />,
              },
              {
                to: '/app/knowledge/proposals',
                label: 'Proposals',
                icon: <GitPullRequest className="h-[18px] w-[18px]" />,
              },
              {
                to: '/app/knowledge/progress',
                label: 'Progress',
                icon: <Activity className="h-[18px] w-[18px]" />,
              },
            ],
          },
          {
            // The parent is the command center; its sub-pages share the /app/growth prefix, so
            // they are excluded here and highlight only themselves. Campaign drill-downs stay
            // under the command center.
            to: '/app/growth',
            label: 'Growth',
            icon: <TrendingUp className="h-[18px] w-[18px]" />,
            excludeActivePaths: ['/app/growth/search-terms', '/app/growth/products', '/app/growth/strategy'],
            children: [
              {
                to: '/app/growth/search-terms',
                label: 'Search terms',
                icon: <Search className="h-[18px] w-[18px]" />,
              },
              {
                to: '/app/growth/products',
                label: 'Products',
                icon: <Package className="h-[18px] w-[18px]" />,
              },
              {
                to: '/app/growth/strategy',
                label: 'Strategy',
                icon: <Target className="h-[18px] w-[18px]" />,
              },
            ],
          },
          {
            to: '/app/feeds',
            label: 'Feeds',
            icon: <Rss className="h-[18px] w-[18px]" />,
          },
          {
            to: '/app/support',
            label: 'Support',
            icon: <Headset className="h-[18px] w-[18px]" />,
          },
          {
            to: '/app/content',
            label: 'Content',
            icon: <LayoutGrid className="h-[18px] w-[18px]" />,
            exact: true,
            children: [
              {
                to: '/app/content/recommendations',
                label: 'Recommendations',
                icon: <Sparkles className="h-[18px] w-[18px]" />,
              },
              {
                to: '/app/content-studio',
                label: 'Studio',
                icon: <Sparkles className="h-[18px] w-[18px]" />,
              },
              {
                to: '/app/content/coverage',
                label: 'Coverage',
                icon: <Layers className="h-[18px] w-[18px]" />,
              },
            ],
          },
        ],
      },
      {
        label: 'Insights',
        items: [
          {
            to: '/app/artist-activity',
            label: 'Artist Activity',
            icon: <BarChart3 className="h-[18px] w-[18px]" />,
          },
        ],
      },
      {
        label: 'Reports',
        items: [
          {
            to: '/app/invoices',
            label: 'Invoices',
            icon: <Receipt className="h-[18px] w-[18px]" />,
          },
          {
            to: '/app/fulfillment',
            label: 'Fulfillment',
            icon: <Truck className="h-[18px] w-[18px]" />,
          },
          {
            to: '/app/export-sales',
            label: 'Export Sales',
            icon: <DollarSign className="h-[18px] w-[18px]" />,
          },
        ],
      },
      {
        label: 'System',
        items: [
          {
            to: '/app/releases',
            label: 'Releases',
            icon: <ShieldCheck className="h-[18px] w-[18px]" />,
          },
          {
            to: '/app/tasks',
            label: 'Task Queue',
            icon: <ListTodo className="h-[18px] w-[18px]" />,
          },
          {
            to: '/app/users',
            label: 'User Admin',
            icon: <UserCog className="h-[18px] w-[18px]" />,
          },
        ],
      },
    ]
  }

  // Artist navigation grouped by intent
  return [
    {
      label: 'Studio',
      items: [
        { to: '/app', label: 'Home', icon: <Home className="h-[18px] w-[18px]" />, exact: true },
        {
          to: '/app/analytics',
          label: 'Insights',
          icon: <BarChart3 className="h-[18px] w-[18px]" />,
          flag: 'studio.insights',
        },
      ],
    },
    {
      label: 'Portfolio',
      items: [
        { to: '/app/easel', label: 'My Portfolio', icon: <Layout className="h-[18px] w-[18px]" /> },
        {
          to: '/app/easel/collections',
          label: 'Collections',
          icon: <Layers className="h-[18px] w-[18px]" />,
        },
        {
          to: '/app/easel/biography',
          label: 'Biography',
          icon: <BookOpen className="h-[18px] w-[18px]" />,
        },
        {
          to: '/app/easel/journal',
          label: 'Journal',
          icon: <FileText className="h-[18px] w-[18px]" />,
        },
        {
          to: '/app/easel/commissions',
          label: 'Commissions',
          icon: <Palette className="h-[18px] w-[18px]" />,
        },
        {
          to: '/app/easel/profile',
          label: 'Link-in-Bio',
          icon: <Link2 className="h-[18px] w-[18px]" />,
        },
        {
          to: '/app/easel/appearance',
          label: 'Appearance',
          icon: <Brush className="h-[18px] w-[18px]" />,
        },
        {
          to: '/app/easel/inbox',
          label: 'Inbox',
          icon: <Mail className="h-[18px] w-[18px]" />,
          flag: 'easel.arthaus_email',
        },
        {
          to: '/app/easel/contact',
          label: 'Messages',
          icon: <Mail className="h-[18px] w-[18px]" />,
        },
        {
          to: '/app/easel/short-links',
          label: 'Short Links',
          icon: <LinkIcon className="h-[18px] w-[18px]" />,
          flag: 'easel.short_links',
        },
        {
          to: '/app/easel/hero',
          label: 'Hero Studio',
          icon: <Image className="h-[18px] w-[18px]" />,
          flag: 'easel.hero_studio',
        },
      ],
    },
    {
      label: 'Social',
      items: [
        // Internal production tools, not artist features yet: both are `off` in the artist shell
        // (migration 202610090001) and will be renamed before they are rolled out. Managers reach
        // them from the Admin menu above, which is not gated.
        {
          to: '/app/films',
          label: 'Film Studio',
          icon: <Sparkles className="h-[18px] w-[18px]" />,
          flag: 'social.film_studio',
        },
        {
          to: '/app/worlds',
          label: 'World Studio',
          icon: <Sparkles className="h-[18px] w-[18px]" />,
          flag: 'social.world_studio',
        },
        {
          to: '/app/social/creator',
          label: 'Post Creator',
          icon: <Sparkles className="h-[18px] w-[18px]" />,
          badge: 'AI',
          flag: 'social.post_creator',
        },
        {
          to: '/app/social/calendar',
          label: 'Calendar',
          icon: <Calendar className="h-[18px] w-[18px]" />,
          flag: 'social.calendar',
        },
      ],
    },
    {
      label: 'Growth',
      items: [
        {
          to: '/app/marketing/email',
          label: 'Email Marketing',
          icon: <Megaphone className="h-[18px] w-[18px]" />,
          flag: 'marketing.email',
        },
        {
          to: '/app/marketing/drops',
          label: 'Drops',
          icon: <Sparkles className="h-[18px] w-[18px]" />,
          flag: 'marketing.drops',
        },
      ],
    },
    {
      label: 'Commerce',
      items: [
        { to: '/app/artworks', label: 'Inventory', icon: <Image className="h-[18px] w-[18px]" /> },
        { to: '/app/sales', label: 'Sales', icon: <DollarSign className="h-[18px] w-[18px]" /> },
        { to: '/app/invoices', label: 'Invoices', icon: <Receipt className="h-[18px] w-[18px]" /> },
        { to: '/app/upload', label: 'Upload Art', icon: <Upload className="h-[18px] w-[18px]" /> },
      ],
    },
  ]
}

function NavItem({
  to,
  label,
  icon,
  collapsed,
  exact,
  excludeActivePaths,
  badge,
  staffOnly,
}: NavItem & { collapsed: boolean; staffOnly?: boolean }) {
  const location = useLocation()
  const matchesPath = exact
    ? location.pathname === to
    : location.pathname === to || location.pathname.startsWith(`${to}/`)
  const isActive =
    matchesPath &&
    !excludeActivePaths?.some(
      (path) => location.pathname === path || location.pathname.startsWith(`${path}/`)
    )

  return (
    <Link
      to={to}
      aria-current={isActive ? 'page' : undefined}
      title={
        staffOnly ? `${label}: visible to you as staff. This artist does not see it.` : undefined
      }
      className={cn(
        'flex items-center gap-3 rounded-md px-3 py-2 text-[14px] font-normal transition-colors relative',
        isActive
          ? 'bg-sidebar-active text-foreground'
          : 'text-muted-foreground hover:bg-sidebar-active/60 hover:text-foreground',
        collapsed && 'justify-center'
      )}
    >
      {icon}
      {!collapsed && (
        <>
          <span className="flex-1">{label}</span>
          {/* Viewing as an artist: this row is here because of the manager, not the artist. */}
          {staffOnly && (
            <span className="ml-auto flex h-4 items-center rounded-full border border-dashed border-muted-foreground/50 px-1.5 text-[10px] font-medium text-muted-foreground">
              Staff
            </span>
          )}
          {badge && !staffOnly && (
            <span className="ml-auto flex h-4 w-auto items-center justify-center rounded-full bg-primary/20 px-1.5 text-[10px] font-bold text-primary">
              {badge}
            </span>
          )}
        </>
      )}
      {collapsed && badge && (
        <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-primary" />
      )}
    </Link>
  )
}

function NavigationSection({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const { pathname } = useLocation()
  const [expanded, setExpanded] = useState(true)
  const sectionId = `admin-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-sections`
  const inSection =
    pathname === item.to ||
    pathname.startsWith(`${item.to}/`) ||
    !!item.children?.some((child) => pathname === child.to || pathname.startsWith(`${child.to}/`))
  useEffect(() => {
    if (inSection) setExpanded(true)
  }, [pathname, inSection])
  if (collapsed)
    return (
      <>
        <NavItem {...item} collapsed />
        {item.children?.map((child) => (
          <NavItem key={child.to} {...child} collapsed />
        ))}
      </>
    )
  return (
    <div>
      <div className="flex items-center gap-1">
        <div className="min-w-0 flex-1">
          <NavItem {...item} collapsed={false} />
        </div>
        <button
          type="button"
          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${item.label} navigation`}
          aria-expanded={expanded}
          aria-controls={sectionId}
          className="flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-sidebar-active focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => setExpanded(!expanded)}
        >
          <ChevronDown
            aria-hidden="true"
            className={cn('h-4 w-4 transition-transform', !expanded && '-rotate-90')}
          />
        </button>
      </div>
      {expanded && (
        <nav
          id={sectionId}
          aria-label={`${item.label} sections`}
          className="ml-5 mt-1 space-y-1 border-l border-border/70 pl-2"
        >
          {item.children?.map((child) => (
            <NavItem key={child.to} {...child} collapsed={false} />
          ))}
        </nav>
      )}
    </div>
  )
}

export function Sidebar({ open, user, onToggle }: SidebarProps) {
  // Commissions is behind a release gate — hide the nav item until it's enabled
  // for this artist. (Managers never see it, so skip the check for them.)
  const { data: commissionsAccess } = useCommissionsAccess({
    enabled: user.role !== UserRole.MANAGER,
  })
  const commissionsEnabled = commissionsAccess?.enabled ?? false

  // Release gating. `isLoading` matters: rendering the full menu and then removing items a moment
  // later would flash unreleased work at every artist on every page load, so gated items stay out
  // until the answer is known.
  const { isEnabled, isStaffOnly, isLoading: flagsLoading } = useFeatures()

  const navigationGroups = getNavigationGroups(user.role)
    .map((group) => ({
      ...group,
      items: group.items
        .filter((item) => {
          if (item.to === '/app/easel/commissions' && !commissionsEnabled) return false
          // Arthaus Email's Inbox replaces Messages (website enquiries are delivered into it);
          // the form history stays reachable from the Inbox's settings menu.
          if (item.to === '/app/easel/contact' && !flagsLoading && isEnabled('easel.arthaus_email')) return false
          if (!item.flag) return true
          return flagsLoading ? false : isEnabled(item.flag)
        })
        // A manager viewing as an artist still sees unreleased rows (so they can be judged in
        // place); mark them, or the shell reads as "this artist has all of this".
        .map((item) => ({ ...item, staffOnly: Boolean(item.flag && isStaffOnly(item.flag)) })),
    }))
    // A group whose every item is gated away would otherwise render as a bare heading over
    // nothing — "Social" with no rows under it still tells the artist something is missing.
    .filter((group) => group.items.length > 0)

  return (
    <aside
      className={cn(
        'bg-sidebar-bg border-r border-border/50 transition-all duration-300 flex flex-col',
        open ? 'fixed inset-y-0 left-0 z-40 w-64 lg:static' : 'hidden w-20 lg:flex'
      )}
    >
      {/* Logo */}
      <div className="h-16 flex items-center justify-center border-b border-border/50 px-4">
        <button
          className="absolute right-3 lg:hidden"
          aria-label="Close navigation"
          onClick={onToggle}
        >
          ×
        </button>
        <h2
          className={cn('font-serif font-semibold tracking-tight', open ? 'text-2xl' : 'text-xl')}
        >
          {open ? 'Arthaus' : 'A'}
        </h2>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-3 space-y-6">
        {navigationGroups.map((group) => (
          <div key={group.label} className="space-y-1">
            {open && (
              <h3 className="px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/50 mb-2">
                {group.label}
              </h3>
            )}
            <div className="space-y-1">
              {group.items.map((item) =>
                item.children ? (
                  <NavigationSection key={item.to} item={item} collapsed={!open} />
                ) : (
                  <NavItem key={item.to} {...item} collapsed={!open} />
                )
              )}
            </div>
          </div>
        ))}

        <Separator className="my-4 bg-border/50" />

        <div className="space-y-1">
          <NavItem
            to="/app/account"
            label="My Account"
            icon={<Users className="h-[18px] w-[18px]" />}
            collapsed={!open}
          />

          <NavItem
            to="/app/signout"
            label="Sign Out"
            icon={<LogOut className="h-[18px] w-[18px]" />}
            collapsed={!open}
          />
        </div>
      </nav>

      {/* User Profile Section */}
      <div className="p-4 border-t border-border/50">
        <div className={cn('flex items-center gap-3', !open && 'justify-center')}>
          <Avatar className="h-9 w-9">
            <AvatarFallback className="bg-muted text-xs font-medium">
              {getInitials(user)}
            </AvatarFallback>
          </Avatar>
          {open && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{formatUserName(user)}</p>
              <p className="text-xs text-muted-foreground truncate">{user.email}</p>
            </div>
          )}
        </div>
      </div>
    </aside>
  )
}
